import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateRange from 'components/Form/DateRange';
import NotApplicable from 'components/Form/NotApplicable';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { Dispatch } from 'redux';
import { LegalAssociationsViolenceItemModel } from 'forms/sections/legal/associations/violence/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import {
  CommonScrollIntoView,
  CommonOnError,
  CommonAddressBooks,
  CommonOnUpdate,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';

interface Props
  extends Partial<LegalAssociationsViolenceItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  scrollIntoView?: CommonScrollIntoView
  required?: boolean
  onError?: CommonOnError
  onUpdate?: CommonOnUpdate
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
}

export default class MembershipViolenceItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalAssociationsViolenceItemModel>) => {
    this.props.onUpdate({
      Organization: this.props.Organization,
      Address: this.props.Address,
      Dates: this.props.Dates,
      PositionsNotApplicable: this.props.PositionsNotApplicable,
      Positions: this.props.Positions,
      ContributionsNotApplicable: this.props.ContributionsNotApplicable,
      Contributions: this.props.Contributions,
      Reasons: this.props.Reasons,
      ...queue,
    });
  };

  updateOrganization = (values: LegalAssociationsViolenceItemModel['Organization']) => {
    this.update({
      Organization: values,
    });
  };

  updateAddress = (values: LegalAssociationsViolenceItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateDates = (values: LegalAssociationsViolenceItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updatePositionsNotApplicable = (
    values: LegalAssociationsViolenceItemModel['PositionsNotApplicable']
  ) => {
    this.update({
      Positions: values.applicable ? this.props.Positions : {},
      PositionsNotApplicable: values,
    });
  };

  updatePositions = (values: LegalAssociationsViolenceItemModel['Positions']) => {
    this.update({
      Positions: values,
    });
  };

  updateContributionsNotApplicable = (
    values: LegalAssociationsViolenceItemModel['ContributionsNotApplicable']
  ) => {
    this.update({
      Contributions: values.applicable ? this.props.Contributions : {},
      ContributionsNotApplicable: values,
    });
  };

  updateContributions = (values: LegalAssociationsViolenceItemModel['Contributions']) => {
    this.update({
      Contributions: values,
    });
  };

  updateReasons = (values: LegalAssociationsViolenceItemModel['Reasons']) => {
    this.update({
      Reasons: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div>
        <Field
          title={i18n.t('legal.associations.violence.heading.organization')}
          attribute={`${attribute}.Organization`}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Organization"
            attribute={`${attribute}.Organization`}
            {...this.props.Organization}
            onUpdate={this.updateOrganization}
            onError={this.props.onError}
            className="legal-associations-violence-organization"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.associations.violence.heading.address')}
          attribute={`${attribute}.Address`}
          optional
          help="legal.associations.violence.help.address"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="Address"
            attribute={`${attribute}.Address`}
            {...this.props.Address}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            className="legal-associations-violence-address"
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Organization"
            dispatch={this.props.dispatch}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.associations.violence.heading.dates')}
          attribute={`${attribute}.Dates`}
          help="legal.associations.violence.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Dates"
            attribute={`${attribute}.Dates`}
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            className="legal-associations-violence-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t('legal.associations.violence.heading.positions')}
          attribute={`${attribute}.Positions`}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="PositionsNotApplicable"
            attribute={`${attribute}.PositionsNotApplicable`}
            {...this.props.PositionsNotApplicable}
            className="legal-associations-violence-positions-na"
            onUpdate={this.updatePositionsNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.associations.violence.para.or')}
            label={i18n.t('legal.associations.violence.label.noposition')}
            required={this.props.required}
            includeNAConfirmation={Boolean(this.props.Positions?.value)}
          >
            <Text
              name="Positions"
              attribute={`${attribute}.Positions`}
              {...this.props.Positions}
              onUpdate={this.updatePositions}
              onError={this.props.onError}
              className="legal-associations-violence-positions"
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('legal.associations.violence.heading.contributions')}
          attribute={`${attribute}.Contributions`}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="ContributionsNotApplicable"
            attribute={`${attribute}.ContributionsNotApplicable`}
            {...this.props.ContributionsNotApplicable}
            className="legal-associations-violence-contributions-na"
            onUpdate={this.updateContributionsNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.associations.violence.para.or')}
            label={i18n.t('legal.associations.violence.label.nocontribs')}
            required={this.props.required}
            includeNAConfirmation={Boolean(this.props.Contributions?.value)}
          >
            <Text
              name="Contributions"
              attribute={`${attribute}.Contributions`}
              {...this.props.Contributions}
              onUpdate={this.updateContributions}
              onError={this.props.onError}
              className="legal-associations-violence-contributions"
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('legal.associations.violence.heading.reasons')}
          attribute={`${attribute}.Reasons`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Reasons"
            attribute={`${attribute}.Reasons`}
            {...this.props.Reasons}
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            className="legal-associations-violence-reasons"
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}

MembershipViolenceItem.defaultProps = {
  PositionsNotApplicable: { applicable: true },
  ContributionsNotApplicable: { applicable: true },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
